import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  CalendarIcon,
  CreditCardIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { getPortalAppointments, getPortalInvoices, getPortalIntakeForms } from '../../services/portalService';
import authStore from '../../store/authStore';
import Badge from '../../components/common/Badge';
import SkeletonBlock from '../../components/common/SkeletonBlock';

const STATUS_VARIANTS = {
  scheduled:   'info',
  confirmed:   'success',
  completed:   'default',
  cancelled:   'error',
  no_show:     'warning',
  rescheduled: 'warning',
};

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

export default function PortalDashboard() {
  const { user } = authStore();

  const { data: apptData, isLoading: apptLoading } = useQuery({
    queryKey: ['portal', 'appointments'],
    queryFn: getPortalAppointments,
  });

  const { data: invoices, isLoading: invoicesLoading } = useQuery({
    queryKey: ['portal', 'invoices'],
    queryFn: getPortalInvoices,
  });

  const { data: intakeForms } = useQuery({
    queryKey: ['portal', 'intake-forms'],
    queryFn: getPortalIntakeForms,
  });

  const outstanding = invoices
    ?.filter((inv) => !['paid', 'cancelled'].includes(inv.status))
    .reduce((sum, inv) => sum + (parseFloat(inv.total) - parseFloat(inv.amountPaid)), 0) ?? 0;

  const nextAppt = apptData?.upcoming?.[0];
  const pendingForms = (intakeForms ?? []).filter((f) => !f.completedAt);

  return (
    <div className="space-y-6">
      {/* Hero welcome card */}
      <div className="rounded-2xl bg-gradient-to-br from-teal-600 to-teal-700 text-white px-6 py-7">
        <p className="text-teal-100 text-sm">{getGreeting()}</p>
        <h1 className="text-2xl font-semibold mt-1 text-white">
          Welcome back, {user?.firstName}
        </h1>
        {apptLoading ? (
          <SkeletonBlock className="h-4 w-48 mt-3 opacity-30" rounded="rounded" />
        ) : nextAppt ? (
          <p className="mt-3 text-sm text-teal-100">
            Your next session is{' '}
            <strong className="text-white">
              {format(new Date(nextAppt.startTime), "EEEE 'at' h:mm a")}
            </strong>
          </p>
        ) : (
          <p className="mt-3 text-sm text-teal-100">
            No upcoming appointments.{' '}
            <Link to="/portal/schedule" className="text-white underline underline-offset-2">
              Schedule one
            </Link>
          </p>
        )}
      </div>

      {/* Pending forms alert */}
      {pendingForms.length > 0 && (
        <div className="card border-l-4 border-l-warning-500 flex items-center justify-between gap-4 py-4">
          <div className="flex items-center gap-3">
            <ClipboardDocumentListIcon className="w-5 h-5 text-warning-500 shrink-0" />
            <div>
              <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                {pendingForms.length} form{pendingForms.length > 1 ? 's' : ''} to complete
              </p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                Required before your next appointment
              </p>
            </div>
          </div>
          <Link to="/portal/intake-forms" className="btn btn-sm btn-primary shrink-0">
            Complete now
          </Link>
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="card border-t-2 border-t-teal-200 dark:border-t-teal-800">
          <div className="flex items-center gap-3 mb-2">
            <CalendarIcon className="h-5 w-5 text-teal-500 shrink-0" />
            <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
              Next appointment
            </p>
          </div>
          {apptLoading ? (
            <SkeletonBlock className="h-5 w-36" rounded="rounded" />
          ) : nextAppt ? (
            <p className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
              {format(new Date(nextAppt.startTime), 'EEE, MMM d · h:mm a')}
            </p>
          ) : (
            <p className="text-sm text-neutral-500">None scheduled</p>
          )}
        </div>

        <div className="card border-t-2 border-t-teal-200 dark:border-t-teal-800">
          <div className="flex items-center gap-3 mb-2">
            <CreditCardIcon className="h-5 w-5 text-teal-500 shrink-0" />
            <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
              Balance due
            </p>
          </div>
          {invoicesLoading ? (
            <SkeletonBlock className="h-5 w-20" rounded="rounded" />
          ) : (
            <p className={`text-base font-semibold ${outstanding > 0 ? 'text-danger-600 dark:text-danger-400' : 'text-neutral-900 dark:text-neutral-100'}`}>
              ${outstanding.toFixed(2)}
            </p>
          )}
        </div>
      </div>

      {/* Upcoming appointments */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2>Upcoming appointments</h2>
          <Link to="/portal/appointments" className="text-sm text-teal-600 dark:text-teal-400 hover:underline">
            View all
          </Link>
        </div>

        {apptLoading ? (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="flex items-center gap-3">
                <SkeletonBlock className="h-10 w-1 shrink-0" rounded="rounded-full" />
                <div className="space-y-1.5 flex-1">
                  <SkeletonBlock className="h-4 w-32" rounded="rounded" />
                  <SkeletonBlock className="h-3 w-48" rounded="rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : !apptData?.upcoming?.length ? (
          <p className="text-sm text-neutral-500 dark:text-neutral-400 py-2">
            No upcoming appointments.{' '}
            <Link to="/portal/schedule" className="text-teal-600 dark:text-teal-400 hover:underline">
              Schedule one
            </Link>
          </p>
        ) : (
          <ul className="divide-y divide-neutral-100 dark:divide-neutral-700">
            {apptData.upcoming.slice(0, 3).map((appt) => (
              <li key={appt.id} className="py-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className="w-1 h-10 rounded-full shrink-0"
                    style={{ backgroundColor: appt.appointmentType?.color || '#0d9488' }}
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                      {appt.appointmentType?.name ?? 'Appointment'}
                    </p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                      {format(new Date(appt.startTime), 'EEEE, MMMM d · h:mm a')}
                    </p>
                  </div>
                </div>
                <Badge
                  label={appt.status.replace('_', ' ')}
                  variant={STATUS_VARIANTS[appt.status] || 'default'}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Recent invoices */}
      {invoices?.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2>Recent invoices</h2>
            <Link to="/portal/invoices" className="text-sm text-teal-600 dark:text-teal-400 hover:underline">
              View all
            </Link>
          </div>
          <ul className="divide-y divide-neutral-100 dark:divide-neutral-700">
            {invoices.slice(0, 3).map((inv) => (
              <li key={inv.id} className="py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{inv.invoiceNumber}</p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                    {format(new Date(inv.invoiceDate), 'MMM d, yyyy')} · ${parseFloat(inv.total).toFixed(2)}
                  </p>
                </div>
                <Badge
                  label={inv.status}
                  variant={inv.status === 'paid' ? 'success' : inv.status === 'overdue' ? 'error' : 'default'}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
